import React from 'react';
import ReactDOM from "react-dom";
import { shuffle } from 'lodash';

// Components
import Sidebar from "react-sidebar";
import { BackgroundType, ConfigPanel, ElementType } from './ConfigPanel';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { Image } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';

// Styles
import '../css/index.css';
import 'bootstrap/dist/css/bootstrap.min.css';

// Fontawesome
import '@fortawesome/fontawesome-free/css/all.css'

// Imgs
import logo from '../gifs/lightning.webp';
import wall from '../imgs/wall.png'
import wallHalloween from '../imgs/wall-halloween.png'
import video from '../videos/party.mp4';

const MILLISECONDS_TO_HIDE_ELEMENTS = 15000 // Number of milliseconds to show the number of the loser until it's cleaned
const MAX_ROUND_COUNT_UNTIL_LIGHTNING = 5; // Count common rounds until a lightning round
const COUNT_LOSERS_FOR_LIGHTNING_ROUND = 5; // Count for looser to show on every lightning round
const DEFAULT_BACKGROUND_TYPE: BackgroundType = 'wall'; // Default background to show


/**
 * Component's state
 */
interface EscabioState {
    countdown: number,
    newCountdown: number,
    loserName: string,
    lastLoserName: string | null,
    drink: string,
    names: string[],
    drinks: string[],
    logoImgBase64: string | ArrayBuffer,
    background: BackgroundType,
    enableLightningRound: boolean,
    showDrink: boolean,
    sidebarOpen: boolean
}

/**
 * Renders main component
 */
class Escabio extends React.Component<{}, EscabioState> {
    private defaultCountdown: number
    private lightningNames: string[]
    private lightning: number
    private commonRoundCurrentCount: number

    constructor(props) {
        super(props);

        this.defaultCountdown = 600

        this.state = {
            countdown: this.defaultCountdown,
            newCountdown: this.secondsToMinutes(this.defaultCountdown),
            loserName: '',
            lastLoserName: null,
            drink: '',
            names: [],
            drinks: [],
            logoImgBase64: '',
            background: DEFAULT_BACKGROUND_TYPE,
            enableLightningRound: true,
            showDrink: false,
            sidebarOpen: false
        };

        // Variables and array for the lightning round
        this.lightningNames = [];
        this.lightning = 0;
        this.commonRoundCurrentCount = 0; // For testing of lightningRound change this value to 3 and de countdown decrease to 10
    }

    /**
     * Reduce a second every second
     */
    componentDidMount() {
        // Loads saved info
        this.loadFromLocalStorage()

        setInterval(() => {
            this.decrease();
        }, 1000);
    }

    /**
     * Gets a key from Local Storage and parses it in JSON format
     * @param key Key to retrieve from Local Storage
     * @param defaultValue Default value to return in case error o key doesn't exist
     * @returns Value stored in Local Storage or the default value
     */
    parseOrDefault<T>(key: string, defaultValue: T): T {
        let res: T
        try {
            const dataRetrieved = window.localStorage.getItem(key)
            res = dataRetrieved ? JSON.parse(dataRetrieved) : defaultValue
        } catch {
            res = defaultValue
        }
        
        return res
    }

    /**
     * Handles changes in newCountdown state
     * @param newCountdown New state vale
     */
    handleCountdownTimeChange = (newCountdown: number) => { this.setState({ newCountdown }) }

    /**
     * Handles changes in the logo input
     * @param e Event
     */
    handleLogoChange = (e) => {
        if (e.target.files && e.target.files.length > 0) {
            const newFile = e.target.files[0]
            
            // Only images
            if (['image/png', 'image/jpeg', 'image/jpg', 'image/webp'].includes(newFile.type)) {
                const reader = new FileReader();
                const self = this
                reader.onloadend = function () {
                    self.setState({ logoImgBase64: reader.result }, self.saveStateInLocalStorage)
                }
                reader.readAsDataURL(newFile);
            }
        }
    }

    /** Cleans the logo img */
    cleanLogoImg = () => { this.setState({ logoImgBase64: '' }, this.saveStateInLocalStorage) }

    /**
     * Transforms seconds to minutes
     * @param seconds Seconds to transform as minutes
     * @return Minutes
     */
    secondsToMinutes = (seconds: number): number => Math.ceil(seconds / 60)
    
    /**
     * Transforms minutes to seconds
     * @param minutes Minutes to transform as seconds
     * @return Seconds
     */
    minutesToSeconds = (minutes: number): number => Math.ceil(minutes * 60)

    /**
     * Sets the new countdown time and restarts some states
     */
    setCountdownTime = () => {
        const newCountdownInSeconds = this.minutesToSeconds(this.state.newCountdown)
        this.setState({ countdown: newCountdownInSeconds }, this.saveStateInLocalStorage)
        this.defaultCountdown = newCountdownInSeconds
        this.lightning = 0
    }

    /**
     * Loads saved data from Local Storage
     */
    loadFromLocalStorage() {
        const savedCountdownInSeconds = this.parseOrDefault('countdown', this.defaultCountdown)
        this.defaultCountdown = savedCountdownInSeconds
        this.setState({
            countdown: savedCountdownInSeconds,
            newCountdown: this.secondsToMinutes(savedCountdownInSeconds),
            names: this.parseOrDefault<string[]>('names', []),
            drinks: this.parseOrDefault<string[]>('drinks', []),
            background: this.parseOrDefault('background', DEFAULT_BACKGROUND_TYPE),
            logoImgBase64: this.parseOrDefault('logoImgBase64', ''),
            enableLightningRound: this.parseOrDefault('enableLightningRound', true),
            showDrink: this.parseOrDefault('showDrink', false),
        }, this.updateLightningNames) // Updates names to prevent empty output on first lightning round
    }

    /** Updates lightningNames variable */
    updateLightningNames = () => {
        this.lightningNames = this.state.names.slice()
    }

    /** Saves all variables in localStorage and updates lightning names */
    saveStorageAndUpdateLightningNames = () => {
        this.updateLightningNames()
        this.saveStateInLocalStorage()
    }
    
    /**
     * Add a name to the list
     * @param newName New name to add
     */
    addName = (newName: string) => {
        let names = this.state.names;
        names.push(newName);
        this.setState({ names }, this.saveStorageAndUpdateLightningNames);
    }

    /**
     * Handles imported names from a CSV file.
     * @param names List of names imported from file.
     * @param replaceNames If true, replaces all the names with the imported ones.
     */
    handleImportedNames = (names: string[], replaceNames: boolean) => {
        if (replaceNames) {
            this.setState({ names }, this.saveStorageAndUpdateLightningNames)
        } else {
            this.setState((previousState) => ({ names: [...previousState.names, ...names] }), this.saveStorageAndUpdateLightningNames)
        }
    }

    /**
     * Removes a specific name by index from the list name
     * @param idx Index to remove from names array
     */
    removeName = (idx: number) => {
        const names = this.state.names
        if (idx < names.length) {
            names.splice(idx, 1);
        }
        this.setState({ names }, this.saveStorageAndUpdateLightningNames)
    }

    /**
     * Add a drink to the list
     * @param newDrink New drink to add
     */
    addDrink = (newDrink: string) => {
        let drinks = this.state.drinks;
        drinks.push(newDrink)
        this.setState({ drinks }, this.saveStateInLocalStorage);
    }

    /**
     * Removes a specific name by index from the list name
     * @param idx Index to remove from drinks array
     */
    removeDrink = (idx: number) => {
        const drinks = this.state.drinks
        if (idx < drinks.length) {
            drinks.splice(idx, 1);
        }
        this.setState({ drinks }, this.saveStateInLocalStorage)
    }

    /** Removes all the names or drinks from the list */
    removeAll = (elements: ElementType) => {
        if (elements === 'names') {
            this.setState({ names: [] }, this.saveStorageAndUpdateLightningNames)
        } else {
            this.setState({ drinks: [] }, this.saveStateInLocalStorage)
        }
    }

    /**
     * Save key and value in Local Storage
     * @param key Key to store
     * @param value Value to store
     */
    saveKeyAndValueInJSON(key: string, value) {
        window.localStorage.setItem(key, JSON.stringify(value))
    }

    /**
     * Saves all the use info state
     */
    saveStateInLocalStorage() {
        this.saveKeyAndValueInJSON('countdown', this.state.countdown)
        this.saveKeyAndValueInJSON('names', this.state.names)
        this.saveKeyAndValueInJSON('drinks', this.state.drinks)
        this.saveKeyAndValueInJSON('background', this.state.background)
        this.saveKeyAndValueInJSON('logoImgBase64', this.state.logoImgBase64)
        this.saveKeyAndValueInJSON('enableLightningRound', this.state.enableLightningRound)
        this.saveKeyAndValueInJSON('showDrink', this.state.showDrink)
    }
    
    /**
     * Reduce countdown value by 1
     */
    decrease() {
        this.setState((previousState) => ({
            countdown: previousState.countdown - 1
        }));
    }

    /**
     * Toogle checkbox inputs' values
     * @param e Checkbox change event
     */
    handleCheckboxChange = (e) => {
        const newValue = e.target.type !== 'checkbox' ? e.target.value : e.target.checked
        this.setState<never>({[e.target.name]: newValue}, this.saveStateInLocalStorage);
    }
    
    /**
     * Gets countdown content to show
     */
    generateCountdown() {
        let countdownDescription;
        
        let divisor_for_minutes = this.state.countdown % (60 * 60);
        let minutes = Math.floor(divisor_for_minutes / 60);
        
        let divisor_for_seconds = divisor_for_minutes % 60;
        let seconds = Math.ceil(divisor_for_seconds);
        const secondsString = (seconds < 10) ? '0' + seconds : seconds;
        
        if (minutes) {
            countdownDescription = minutes + ':' + secondsString + ' minutos';
        } else {
            countdownDescription = secondsString + ' segundos';
        }

        // It's time to drink!
        if (!minutes && secondsString == '00') {
            // Normal round
            if (!this.state.enableLightningRound || this.commonRoundCurrentCount < MAX_ROUND_COUNT_UNTIL_LIGHTNING) {
                this.getLoser();
                this.commonRoundCurrentCount++;
            } else {
                // Lightning round
                if (this.lightning < COUNT_LOSERS_FOR_LIGHTNING_ROUND) {
                    this.lightningRound();
                }
            }
        }    
        
        return countdownDescription;
    }

    /**
     * Gets a random element from an array. Shuffles an array and gets the first element.
     * This is more robust than doing Math.random() * length as it avoids getting non-uniform distributions.
     * @param array Array to get the random element from
     * @returns Random element from the array
     */
    getRandomElementFromArray<T>(array: T[]): T {
        return shuffle(array)[0];
    }

    /**
     * Select randomly a loser in a common (normal) round
     */
    getLoser() {
        
        // The temporary list is created by removing the last name chosen
        const filteredList = this.state.names.filter(name =>  name !== this.state.lastLoserName );

        // If the filtered list is empty i use the original one
        const listToDraw = filteredList.length > 0 ? filteredList : this.state.names;

        const actLoserName = this.getRandomElementFromArray(listToDraw)
        const randomDrink = this.getRandomElementFromArray(this.state.drinks);

        this.setState({
            countdown: this.defaultCountdown,
            loserName: actLoserName,
            lastLoserName: actLoserName,
            drink: randomDrink
        });
        
        // Hides name in some seconds
        setTimeout(() => {
            this.setState({
                loserName: '',
                drink: ''
            });
        }, MILLISECONDS_TO_HIDE_ELEMENTS);
    }

    /**
     * Executes a lightning round logic
     */
    lightningRound(){
        const loserName = this.getRandomElementFromArray(this.lightningNames);
        const randomDrink =  this.getRandomElementFromArray(this.state.drinks);

        // In case of having passed all reload all the names
        if (this.lightningNames.length == 1){
            this.lightningNames = this.state.names.slice();
        }

        let countdownBetweenRounds; // Time between rounds

        // Deletes the name to not repeat
        this.lightningNames = this.lightningNames.filter(name => name !== loserName);

        // Last lightning round
        if (this.lightning === (COUNT_LOSERS_FOR_LIGHTNING_ROUND - 1)) {
            // Resets variables
            countdownBetweenRounds = this.defaultCountdown
            this.commonRoundCurrentCount = 0;
            this.lightning = 0;
            this.updateLightningNames()

            // Reset loser's name
            setTimeout(() => {
                this.setState({
                    loserName: '',
                    drink: ''
                });
            }, MILLISECONDS_TO_HIDE_ELEMENTS);
        } else {
            this.lightning++;

            // During lightning rounds, there're 5 seconds between each loser
            countdownBetweenRounds = 5;
        }

        // Shows loser
        this.setState({
            countdown: countdownBetweenRounds,
            loserName: loserName,
            drink: randomDrink
        });
    }

    /**
     * Toggle sidebar's status
     * @param sidebarOpen New state for sidebar
     */
    onSetSidebarOpen = (sidebarOpen: boolean) => { this.setState({ sidebarOpen }); }

    /**
     * Checks if correspond to show a lightning round GIF as background
     * NOTE: only shows the GIF in last 30 seconds
     */
    shouldShowLightningRoundBackground() {
        return this.state.enableLightningRound
            && this.commonRoundCurrentCount >= MAX_ROUND_COUNT_UNTIL_LIGHTNING
            && this.state.countdown <= 30;
    }

    /**
     * Returns lightning round background img
     */
    getLightningRoundImg() {
        return {
            backgroundImage: `url(${logo})`,
            backgroundSize: 'cover'
        };
    }

    getSpecialRoundDescription() {
        if (!this.state.loserName && this.shouldShowLightningRoundBackground()) {
            return 'Relámpago: fondean 5 personas!';
        }

        return null;
    }
    
    render() {        
        // Gets background img
        const isLightningRound = this.shouldShowLightningRoundBackground()
        let backgroundImage 
        if (isLightningRound) {
            backgroundImage = this.getLightningRoundImg()
        } else {
            if (this.state.background === 'wall') {
                backgroundImage = {
                    backgroundImage: `url(${wall})`,
                    backgroundSize: 'cover'
                }
            } else {
                if (this.state.background === 'wall-halloween') {
                    backgroundImage = {
                        backgroundImage: `url(${wallHalloween})`,
                        backgroundSize: 'cover'
                    }
                } else {
                    backgroundImage = {}
                }
            }
        }
        
        let divClass: string
        switch (this.state.background) {
            case 'wall':
            case 'wall-halloween':
                divClass = 'with-background-wall'
                break
            case 'video':
                divClass = 'with-background-video'
                break
            default:
                divClass = ''
                break
        }

        if (isLightningRound) {
            divClass += ' lightning-round'
        }

        // If it's a special round, show description
        const specialRoundDescription = this.getSpecialRoundDescription();

        return (
            <div id="app-div" className={divClass} style={backgroundImage}>
                {this.state.background === 'video' &&
                    <video id="background-video" autoPlay loop muted disablePictureInPicture hidden={isLightningRound}>
                        <source src={video} type="video/mp4" />
                    </video>
                }

                <Row>
                    <Col md={12} id="div-main-content" className={`text-center mt-2 ${divClass}`}>
                        <h1 id="drink-label">Fondo en</h1>
                        <h1 id="timer">{this.generateCountdown()}</h1>

                        {/* Special round description */}
                        {specialRoundDescription &&
                            <h1 id="special-round-description">{specialRoundDescription}</h1>
                        }

                        {/* Loser name */}
                        {this.state.loserName &&
                            <h1 id="loser">
                                {/* Victim */}
                                <strong className="danger flicker-animation">{this.state.loserName}</strong> en la pera <span style={{ fontSize: '0.3em', whiteSpace: 'nowrap', display: 'inline-block' }}>🤜🏻🍐</span>&nbsp;        
                                
                                {/* Drink */}
                                {this.state.showDrink &&
                                    <>
                                        con&nbsp;
                                        <strong className="danger">{this.state.drink}</strong>
                                    </>
                                }
                            </h1>
                        }
                    </Col>
                </Row>

                {/* Sidebar with config panel */}
                <Sidebar
                    sidebar={
                        <ConfigPanel
                            newCountdown={this.state.newCountdown}
                            names={this.state.names}
                            drinks={this.state.drinks}
                            logoImgBase64={this.state.logoImgBase64}
                            handleCountdownTimeChange={this.handleCountdownTimeChange}
                            setCountdownTime={this.setCountdownTime}
                            addName={this.addName}
                            removeName={this.removeName}
                            addDrink={this.addDrink}
                            removeDrink={this.removeDrink}
                            background={this.state.background}
                            enableLightningRound={this.state.enableLightningRound}
                            showDrink={this.state.showDrink}
                            handleCheckboxChange={this.handleCheckboxChange}
                            handleLogoChange={this.handleLogoChange}
                            cleanLogoImg={this.cleanLogoImg}
                            handleImportedNames={this.handleImportedNames}
                            removeAll={this.removeAll}
                        />
                    }
                    touchHandleWidth={20}
                    dragToggleDistance={30}
                    open={this.state.sidebarOpen}
                    onSetOpen={this.onSetSidebarOpen}
                    styles={{ sidebar: { background: "white" } }}>
                    <Button variant="light" onClick={() => this.onSetSidebarOpen(true)}>
                        <i id="config-button" className="fas fa-cog"></i>
                    </Button>
                </Sidebar>

                {this.state.logoImgBase64 &&
                    <Image id='logo-img' rounded src={this.state.logoImgBase64 as string} />
                }
            </div>
        );
    }
}

// Renders the component
ReactDOM.render(
    <Escabio />,
    document.getElementById('countdown')
);
    